import { ReactNode, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { PartnerSignInBanner } from 'components/organisms/partner-consent'
import { clearPendingPartnerConsent } from 'services/auth'
import {
	declinePartnerConsent,
	describePartnerConsent,
	PARTNER_CONSENT_UNAVAILABLE,
	PartnerConsentDescription,
	PartnerConsentRequest,
} from 'services/partner-consent'
import { goToExternalUrl } from 'utils/external-navigation'
import type { PartnerSignInState } from 'utils/partner-consent-request'

interface PartnerSignInFrameProps {
	request: PartnerConsentRequest
	/** What the page asks for: "Sign in" or "Sign up". */
	action: string
	/** The way back shown when the backend does not recognise the link. */
	fallback: ReactNode
	children: ReactNode
}

/**
 * Frames the auth card for someone on their way to connect a partner: who is asking above it, and a way back to the
 * partner below it. A link the backend does not recognise gets no banner, only the usual way back.
 */
export function PartnerSignInFrame({ request, action, fallback, children }: Readonly<PartnerSignInFrameProps>) {
	const location = useLocation()
	const seed = (location.state as PartnerSignInState | null)?.partnerConsent
	// Undefined while the link is being described; null once it is known not to be recognised.
	const [partner, setPartner] = useState<PartnerConsentDescription | null | undefined>(seed)
	const [leaving, setLeaving] = useState(false)
	const [problem, setProblem] = useState<string | null>(null)

	useEffect(() => {
		// The consent page described this link just before sending the visitor here.
		if (seed) return
		let current = true
		void describePartnerConsent(request).then(result => {
			if (current) setPartner(result.ok ? result.value : null)
		})
		return () => {
			current = false
		}
	}, [request.clientId, request.redirectUri, request.scope, request.state])

	useEffect(() => {
		const onShow = (event: PageTransitionEvent) => {
			if (event.persisted) setLeaving(false)
		}
		window.addEventListener('pageshow', onShow)
		return () => window.removeEventListener('pageshow', onShow)
	}, [])

	const cancel = async () => {
		setLeaving(true)
		setProblem(null)
		const result = await declinePartnerConsent(request)
		if (result.ok) {
			clearPendingPartnerConsent()
			if (goToExternalUrl(result.value.redirectUrl)) return
		}
		setLeaving(false)
		setProblem(result.ok ? PARTNER_CONSENT_UNAVAILABLE.message : result.problem.message)
	}

	return (
		<>
			{partner === undefined && (
				// Holds the banner's place so the card does not move when it arrives.
				<div aria-hidden="true" className="invisible w-full max-w-md">
					<PartnerSignInBanner partnerName="Partner" action={action} />
				</div>
			)}
			{partner && (
				<PartnerSignInBanner partnerName={partner.applicationName} logo={partner.logo} action={action} />
			)}
			{children}
			<div className="mt-4 min-h-6 w-full max-w-md text-balance text-center">
				{partner && (
					<>
						<button
							type="button"
							onClick={cancel}
							disabled={leaving}
							className="-my-3 py-3 text-sm text-secondary-text transition-colors hover:text-primary-text disabled:opacity-50"
						>
							Cancel and go back to {partner.applicationName}
						</button>
						{problem && (
							<p role="alert" className="mt-2 text-sm text-secondary-red">
								{problem}
							</p>
						)}
					</>
				)}
				{partner === null && fallback}
			</div>
		</>
	)
}
